/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Modal,
  Spin,
  message,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import { getCodigosPostales, getEstados, getMunicipios } from 'api/catalogos';
import Select from 'components/Select';

const rules = { required: [{ required: true }] };

const FormBusquedaCodigoPostal = ({
  loading,
  filteredStates,
  dataSource,
  currentPage,
  callback,
  setSelectedRowKeys,
  selectedRowKeys,
  search,
}) => {
  const municipios = useRef({ results: [] });
  const form = Form.useFormInstance();
  const estado = Form.useWatch('estado', form);
  const municipio = Form.useWatch('municipio', form);

  const columns = [
    {
      titleText: 'Código',
      dataIndex: 'd_codigo',
      key: 'd_codigo',
      render: (d_codigo, record) => (
        <Button
          type="link"
          className="primary-text no-color"
          onClick={() => callback(record)}
          style={{ padding: 0 }}
        >
          {d_codigo}
        </Button>
      ),
    },
    {
      titleText: 'Asentamiento',
      dataIndex: 'd_asenta',
      key: 'd_asenta',
    },
    {
      titleText: 'Tipo de Asentamiento',
      dataIndex: 'd_tipo_asenta',
      key: 'd_tipo_asenta',
      width: 200,
    },
    {
      titleText: 'Municipio',
      dataIndex: 'nom_mun',
      key: 'nom_mun',
    },
    {
      titleText: 'Estado',
      dataIndex: 'nombre_de_AGEE',
      key: 'nombre_de_AGEE',
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const changeState = async (stateID) => {
    form.setFieldsValue({
      estado: stateID,
      municipio: undefined,
    });
  };

  return (
    <>
      <Spin tip="Cargando..." spinning={loading}>
        <Row gutter={10}>
          <Col className="gutter-row" md={6} sm={24} xs={24}>
            <Form.Item label="Estado" name="estado" rules={rules.required}>
              <Select dataSource={filteredStates} onChange={(s) => changeState(s)} labelProp="nombre_de_AGEE" />
            </Form.Item>
          </Col>
          <Col className="gutter-row" md={6} sm={24} xs={24}>
            <Form.Item label="Municipio" name="municipio" rules={rules.required}>
              <Select
                dataSource={municipios.current}
                labelProp="nom_mun"
                pagination={{
                  getter: getMunicipios,
                  setter: (data) => {
                    municipios.current = data;
                  },
                  params: { estado },
                  config: { auto: false, qName: 'nom_mun', selfValue: municipio },
                }}
                disabled={!estado}
              />
            </Form.Item>
          </Col>

          <Col md={6} sm={24} xs={24}>
            <Form.Item label="Asentamiento/Colonia" name="asentamiento">
              <Input allowClear />
            </Form.Item>
          </Col>
          <Col md={6} sm={24} xs={24}>
            <Form.Item>
              <Button
                type="link"
                htmlType="submit"
                style={{ marginTop: 20 }}
                className="primary-on-hover"
              >
                <SearchOutlined />
                {' '}
                Buscar
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Spin>
      <Col span={24}>
        <Table
          cols={columns}
          data={dataSource?.results}
          noControls
          allowSort={false}
          allowSearch={false}
          rowSelection={rowSelection}
          onRow={(record) => ({
            onDoubleClick: () => callback(record),
            onClick: () => setSelectedRowKeys([record.key]),
          })}
          currentPage={currentPage}
          pagination={false}
          paginationCallback={search}
          count={dataSource.count}
        />
      </Col>
    </>
  );
};

const BusquedaCodigoPostal = ({
  callback,
  setVisible,
  visible,
  addressPropName,
  modal,
}) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [dataSource, setDataSource] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filteredStates, setFilteredStates] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const search = async (page = 1, q = null) => {
    setLoading(true);
    setCurrentPage(page);
    const { asentamiento, municipio } = form.getFieldsValue();
    const _data = await getCodigosPostales({
      page,
      d_asenta: asentamiento,
      municipio,
      ...q,
    });
    const results = _data.results.map((item) => ({
      ...item,
      key: item.id,
      nom_mun: item.municipio.nom_mun,
      nombre_de_AGEE: item.municipio.estado.nombre_de_AGEE,
    }));
    setDataSource({ ..._data, results });
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      setLoading(true);
      const _estados = await getEstados();
      if (mounted) {
        setFilteredStates(_estados);
        setLoading(false);
      }
    };
    if (visible) {
      fetchData();
    }
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [visible]);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setDataSource([]);
    // municipios.current = { results: [] };
  };

  if (!modal) {
    return (
      <Form
        name={`search_${addressPropName}`}
        layout="vertical"
        form={form}
        onFinish={() => search()}
        scrollToFirstError
      >
        <FormBusquedaCodigoPostal
          loading={loading}
          filteredStates={filteredStates}
          dataSource={dataSource}
          currentPage={currentPage}
          callback={callback}
          setSelectedRowKeys={setSelectedRowKeys}
          selectedRowKeys={setSelectedRowKeys}
          search={search}
        />
      </Form>
    );
  }

  return (
    <Modal
      width="90%"
      open={visible}
      closable={false}
      footer={null}
      title={(
        <FormSubmitControls
          label="Búsqueda Código Postal"
          onFinish={async () => {
            const [key] = selectedRowKeys;
            if (key) {
              const _match = await getCodigosPostales({ id: key });
              callback(_match.results);
            } else {
              message.warn('Seleccione un elemento');
            }
          }}
          onCancel={onCancel}
          loading={loading}
        />
      )}
      onCancel={onCancel}
      keyboard={!loading}
      maskClosable={!loading}
      forceRender
      centered
      destroyOnClose
    >
      <Form
        name={`search_${addressPropName}`}
        layout="vertical"
        form={form}
        onFinish={() => search()}
        scrollToFirstError
      >
        <FormBusquedaCodigoPostal
          loading={loading}
          filteredStates={filteredStates}
          dataSource={dataSource}
          currentPage={currentPage}
          callback={callback}
          setSelectedRowKeys={setSelectedRowKeys}
          selectedRowKeys={selectedRowKeys}
          search={search}
        />
      </Form>
    </Modal>
  );
};

BusquedaCodigoPostal.propTypes = {
  callback: PropTypes.func.isRequired,
  visible: PropTypes.bool,
  setVisible: PropTypes.func.isRequired,
  addressPropName: PropTypes.string.isRequired,
  modal: PropTypes.bool,
};

BusquedaCodigoPostal.defaultProps = {
  visible: false,
  modal: false,
};

export default BusquedaCodigoPostal;
